<template>
  <div class="dg">
    <div class="dg-head">
      <div class="dg-title">
        When do Breeze dates arrive?
      </div>
      <div class="dg-tabs">
        <button
          v-for="t in tabs"
          :key="t.k"
          type="button"
          class="dg-tab"
          :class="{ active: view === t.k }"
          @click="view = t.k"
        >{{ t.label }}</button>
      </div>
    </div>

    <div
      v-if="!slots.length"
      class="dg-empty"
    >No arrival demand yet.</div>

    <div
      v-else
      class="dg-bars"
    >
      <div
        v-for="s in slots"
        :key="s.i"
        class="dg-col"
      >
        <div
          class="dg-bar"
          :title="barTitle(s)"
          :style="{ height: barHeight(s) + 'px', background: isPeak(s) ? colors.orange : colors.orange + '33' }"
        />
        <span
          class="dg-tick"
          :class="{ peak: isPeak(s) }"
        >{{ s.m === 0 ? String(s.h) : '' }}</span>
      </div>
    </div>

    <div
      v-if="slots.length"
      class="dg-cap"
    >
      <span class="dg-dot" />
      <div
        v-if="peaks.length"
        class="dg-cap-text"
      >
        {{ viewLabel }} demand peaks at <strong>{{ peakList }}</strong>, which account for
        <strong>{{ peakShare }}%</strong> of {{ viewLabel.toLowerCase() }} dates. Missing these hours means
        fewer dates.
      </div>
      <div
        v-else
        class="dg-cap-text"
      >
        {{ viewLabel }} demand is spread fairly evenly across the day.
      </div>
    </div>
  </div>
</template>

<script>
import { colors } from '@/design-system'

export default {
  name: 'DemandGraph',
  props: {
    // each: [{ slot: 'HH:MM', count }, ...]
    weekdaySlots: {
      type: Array,
      default: () => [],
    },
    weekendSlots: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      view: 'wk',
      colors,
      tabs: [{ k: 'wk', label: 'Weekdays' }, { k: 'we', label: 'Weekends' }],
    }
  },
  computed: {
    slots() {
      const src = this.view === 'wk' ? this.weekdaySlots : this.weekendSlots
      return src.map((s, i) => {
        const [h, m] = s.slot.split(':').map(Number)
        return {
          i, h, m, v: s.count,
        }
      })
    },
    maxVal() {
      return Math.max(1, ...this.slots.map(s => s.v))
    },
    total() {
      return this.slots.reduce((a, s) => a + s.v, 0) || 1
    },
    peaks() {
      return this.slots.filter(s => s.v / this.total >= 0.10)
    },
    viewLabel() {
      return this.view === 'wk' ? 'Weekday' : 'Weekend'
    },
    // "18:00, 19:00 and 20:00"
    peakList() {
      const labels = this.peaks
        .slice()
        .sort((a, b) => (a.h - b.h) || (a.m - b.m))
        .map(s => `${String(s.h).padStart(2, '0')}:${String(s.m).padStart(2, '0')}`)
      if (labels.length <= 1) return labels.join('')
      return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
    },
    peakShare() {
      const sum = this.peaks.reduce((a, s) => a + s.v, 0)
      return Math.round((sum / this.total) * 100)
    },
  },
  methods: {
    isPeak(s) {
      return this.peaks.some(p => p.i === s.i)
    },
    barHeight(s) {
      return Math.max(2, (Math.sqrt(s.v) / Math.sqrt(this.maxVal)) * 78)
    },
    barTitle(s) {
      const share = Math.round((s.v / this.total) * 100)
      const label = share < 1 && s.v > 0 ? '<1' : share
      const slot = `${String(s.h).padStart(2, '0')}:${String(s.m).padStart(2, '0')}`
      return `${slot}: ${label}% of dates`
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/tokens';

.dg {
  background: $c-white;
  border: 1px solid $c-border;
  border-radius: 12px;
  padding: 13px 16px 11px;
  margin-bottom: 12px;
}

.dg-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.dg-title {
  font-weight: 600;
  font-size: 13px;
  color: $c-wine-mid;
}

.dg-tabs {
  display: flex;
  gap: 2px;
  background: $c-bg;
  border: 1px solid $c-border-light;
  border-radius: 8px;
  padding: 2px;
}

.dg-tab {
  padding: 4px 11px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: $c-text-sec;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;

  &.active {
    background: $c-wine;
    color: #ffffff;
  }
}

.dg-bars {
  display: flex;
  align-items: flex-end;
  gap: 1.5px;
  height: 86px;
}

.dg-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 86px;
  color: $c-text-sec;
  font-size: 13px;
}

.dg-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  height: 100%;
}

.dg-bar {
  width: 100%;
  max-width: 11px;
  border-radius: 2px 2px 0 0;
}

.dg-tick {
  font-family: 'DM Mono', monospace;
  font-size: 9.5px;
  color: $c-text-ter;
  font-weight: 400;
  height: 11px;
  line-height: 1;

  &.peak {
    color: $c-text;
    font-weight: 600;
  }
}

.dg-cap {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: 11px;
  padding-top: 10px;
  border-top: 1px solid $c-border-light;
}

.dg-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: $c-orange;
  margin-top: 5px;
  flex-shrink: 0;
}

.dg-cap-text {
  font-size: 11.5px;
  color: $c-text-sec;
  line-height: 1.5;

  strong { color: $c-text; }
}
</style>
